import { Injectable, inject } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { EMPTY, Observable, catchError, of, tap } from 'rxjs';
import { UserRole } from '@shared/enums/user-role.enum';
import { NotificationService } from '@shared/services/notification.service';
import { UserContext } from '@core/models/user-context.model';
import { UserContextService } from '@core/services/user-context.service';
import { LoadUserContext } from './user-context.actions';
import { UserContextStateModel } from './user-context.model';

@State<UserContextStateModel>({
    name: 'userContext',
    defaults: {
        loaded: false,
        role: UserRole.APPRENTICE,
        country: null
    }
})
@Injectable()
export class UserContextState {
    private readonly userContextService = inject(UserContextService);
    private readonly notificationService = inject(NotificationService);

    @Selector()
    static loaded(state: UserContextStateModel): boolean {
        return state.loaded;
    }

    @Selector()
    static role(state: UserContextStateModel): UserRole {
        return state.role;
    }

    @Selector()
    static country(state: UserContextStateModel): string | null {
        return state.country;
    }

    @Selector()
    static canManageMethods(state: UserContextStateModel): boolean {
        return state.role === UserRole.PM || state.role === UserRole.ADMIN;
    }

    @Selector()
    static canCreateNuggets(state: UserContextStateModel): boolean {
        return state.role !== UserRole.APPRENTICE;
    }

    /** The backend only lets admins delete comments of other users. */
    @Selector()
    static canModerateComments(state: UserContextStateModel): boolean {
        return state.role === UserRole.ADMIN;
    }

    @Action(LoadUserContext)
    public loadUserContext(ctx: StateContext<UserContextStateModel>): Observable<UserContext | null> {
        if (ctx.getState().loaded) {
            return of(null);
        }

        return this.userContextService.loadContext().pipe(
            tap(context => ctx.patchState({ ...context, loaded: true })),
            catchError(() => {
                this.notificationService.showError('Benutzerkontext konnte nicht geladen werden');
                return EMPTY;
            })
        );
    }
}
